import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { FileText, Save, Plus, Trash2, ShieldAlert } from 'lucide-react';

// Configuración del DISEÑO de documentos (recibos / facturas / notas) por empresa.
// El propietario elige formato de papel, plantilla/estilo, logo, encabezado y datos extra.
// La marca SalesPRO + 7-study.com aparece SIEMPRE discreta, salvo que el Super Master la oculte.
export default function DocConfig() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const r = await api.getCompanies();
      const list = Array.isArray(r.data) ? r.data : (r.data?.companies || []);
      setCompanies(list);
      if (list[0]) setCompanyId(list[0].company_id);
    } catch (e) { toast.error('SalesPRO: No se pudieron cargar las empresas'); }
  })(); }, []);

  useEffect(() => { if (!companyId) return; (async () => {
    try { const r = await api.getDocConfig(companyId); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); setData(null); }
  })(); }, [companyId]);

  const cfg = data?.doc_config || {};
  const setCfg = (k, v) => setData({ ...data, doc_config: { ...cfg, [k]: v } });
  const setField = (i, k, v) => {
    const ef = [...(cfg.extra_fields || [])];
    ef[i] = { ...ef[i], [k]: v };
    setCfg('extra_fields', ef);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { doc_config: cfg };
      if (data.can_toggle_mark) payload.hide_salespro_mark = !!data.hide_salespro_mark;
      const r = await api.saveDocConfig(companyId, payload);
      setData({ ...data, ...r.data });
      toast.success('SalesPRO: Diseño de documentos guardado');
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="doc-config-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-[#022C22] flex items-center gap-2"><FileText size={22} /> Diseño de Documentos</h1>
        <p className="text-sm text-[#64748B]">Personaliza recibos, facturas y notas de entrega: papel, estilo, logo y datos propios.</p>
      </div>

      <div className="max-w-xs">
        <Label className="text-xs">Empresa / sucursal</Label>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm" data-testid="doc-config-company">
          {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
        </select>
      </div>

      {data && (
        <Card data-testid="doc-config-form"><CardContent className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Formato de papel</Label>
              <select value={cfg.paper} onChange={(e) => setCfg('paper', e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm" data-testid="doc-config-paper">
                {(data.papers || []).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Plantilla / estilo</Label>
              <select value={cfg.template} onChange={(e) => setCfg('template', e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm" data-testid="doc-config-template">
                {(data.templates || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Color de acento (opcional, ej. #0A192F)</Label>
              <Input value={cfg.accent || ''} onChange={(e) => setCfg('accent', e.target.value)} placeholder="#0A192F" data-testid="doc-config-accent" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={cfg.show_logo !== false} onCheckedChange={(v) => setCfg('show_logo', v)} data-testid="doc-config-show-logo" />
              <Label className="text-xs">Mostrar logo de la empresa</Label>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Encabezado adicional (debajo del nombre)</Label>
              <Input value={cfg.header_note || ''} onChange={(e) => setCfg('header_note', e.target.value)} placeholder="Ej: Servicio técnico autorizado" data-testid="doc-config-header" />
            </div>
            <div>
              <Label className="text-xs">Nota de pie adicional</Label>
              <Input value={cfg.footer_note || ''} onChange={(e) => setCfg('footer_note', e.target.value)} placeholder="Ej: Horario L-V 9am-6pm" data-testid="doc-config-footer" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Datos extra del documento (hasta 8)</Label>
              <Button size="sm" variant="ghost" onClick={() => setCfg('extra_fields', [...(cfg.extra_fields || []), { label: '', value: '' }])} data-testid="doc-config-add-field"><Plus size={14} /> Agregar</Button>
            </div>
            {(cfg.extra_fields || []).map((ef, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_40px] gap-2 mb-1">
                <Input placeholder="Etiqueta" value={ef.label || ''} onChange={(e) => setField(i, 'label', e.target.value)} data-testid={`doc-config-field-label-${i}`} />
                <Input placeholder="Valor" value={ef.value || ''} onChange={(e) => setField(i, 'value', e.target.value)} data-testid={`doc-config-field-value-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => setCfg('extra_fields', (cfg.extra_fields || []).filter((_, j) => j !== i))}><Trash2 size={14} className="text-red-500" /></Button>
              </div>
            ))}
          </div>

          <div className="border-t pt-4">
            {data.can_toggle_mark ? (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="doc-config-mark-toggle">
                <ShieldAlert size={18} className="text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Switch checked={!!data.hide_salespro_mark} onCheckedChange={(v) => setData({ ...data, hide_salespro_mark: v })} data-testid="doc-config-hide-mark" />
                    <Label className="text-sm font-semibold text-amber-800">Ocultar marca “SalesPRO · 7-study.com” en los documentos de esta empresa</Label>
                  </div>
                  <p className="text-[11px] text-amber-700 mt-1">Control exclusivo del Super Master. Por defecto, la marca aparece discreta en el pie de cada documento.</p>
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-[#94A3B8]">La marca “SalesPRO · 7-study.com” aparece de forma discreta en el pie de cada documento (no removible).</p>
            )}
          </div>

          <Button onClick={save} disabled={saving} data-testid="doc-config-save"><Save size={15} className="mr-1" /> {saving ? 'Guardando…' : 'Guardar diseño'}</Button>
        </CardContent></Card>
      )}
    </div>
  );
}
